import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Star, Save, X } from "lucide-react";
import api from "@/api";

const euro = (v) => Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const norm = (v) => String(v || "").trim().toLocaleLowerCase("it-IT").replace(/\s+/g, " ");
const emptyPrice = { fornitore: "", tipo: "materiale", descrizione: "", codice: "", unita_misura: "pz", prezzo_unitario: "", aliquota_iva: 22, data_validita: "", note: "" };

export default function FornitoriCostiRating({ fornitori = [] }) {
  const qc = useQueryClient();
  const [section, setSection] = useState("listini");
  const [search, setSearch] = useState("");
  const [supplier, setSupplier] = useState("");
  const [editing, setEditing] = useState(null);
  const [ratingSupplier, setRatingSupplier] = useState("");
  const [ratingForm, setRatingForm] = useState({ qualita: 3, prezzo: 3, puntualita: 3, affidabilita: 3, note: "" });

  const { data: prices = [], isLoading: loadingPrices } = useQuery({
    queryKey: ["fornitori-costi-listini"],
    queryFn: () => api.get("/fornitori-costi/listini").then(r => r.data),
  });
  const { data: ratings = [] } = useQuery({
    queryKey: ["fornitori-costi-rating"],
    queryFn: () => api.get("/fornitori-costi/rating").then(r => r.data),
  });
  const { data: bolle = [], isLoading: loadingBolle } = useQuery({
    queryKey: ["fornitori-costi-bolle"],
    queryFn: () => api.get("/fornitori-costi/confronto-bolle").then(r => r.data),
    enabled: true,
  });

  const suppliers = useMemo(() => [...new Set([
    ...fornitori.map(f => f.ragione_sociale),
    ...prices.map(p => p.fornitore),
    ...ratings.map(r => r.fornitore),
    ...bolle.map(b => b.fornitore),
  ].filter(Boolean))].sort((a, b) => a.localeCompare(b, "it")), [fornitori, prices, ratings, bolle]);

  const savePrice = useMutation({
    mutationFn: ({ id, data }) => id
      ? api.put(`/fornitori-costi/listini/${id}`, data)
      : api.post("/fornitori-costi/listini", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fornitori-costi-listini"] });
      setEditing(null);
      toast.success("Prezzo salvato");
    },
    onError: e => toast.error(e.response?.data?.detail || "Errore nel salvataggio"),
  });
  const deletePrice = useMutation({
    mutationFn: id => api.delete(`/fornitori-costi/listini/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fornitori-costi-listini"] });
      toast.success("Prezzo eliminato");
    },
    onError: () => toast.error("Impossibile eliminare il prezzo"),
  });
  const saveRating = useMutation({
    mutationFn: data => api.put("/fornitori-costi/rating", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fornitori-costi-rating"] });
      toast.success("Valutazione salvata");
    },
    onError: () => toast.error("Impossibile salvare la valutazione"),
  });

  const filteredPrices = prices.filter(p =>
    (!supplier || norm(p.fornitore) === norm(supplier)) &&
    [p.descrizione, p.fornitore, p.codice, p.tipo].some(v => norm(v).includes(norm(search)))
  );
  const filteredBolle = bolle.filter(b =>
    (!supplier || norm(b.fornitore) === norm(supplier)) &&
    [b.materiale, b.fornitore, b.numero_bolla].some(v => norm(v).includes(norm(search)))
  );

  const matchingPrice = b => {
    const matches = prices.filter(p =>
      p.tipo === "materiale" &&
      norm(p.fornitore) === norm(b.fornitore) &&
      norm(p.descrizione) === norm(b.materiale) &&
      norm(p.unita_misura) === norm(b.unita_misura)
    );
    return matches.length === 1 ? matches[0] : null;
  };

  const inputClass = "w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white";
  const buttonClass = "px-3 py-2 text-sm font-semibold rounded border border-slate-300 hover:bg-slate-50";

  return (
    <div className="space-y-5" data-testid="fornitori-costi-rating">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">Costi e Rating Fornitori</h2>
          <p className="text-xs text-slate-500 mt-1">Listini e prezzi delle bolle espressi al netto IVA.</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded text-sm" onClick={() => setEditing({ ...emptyPrice, fornitore: supplier })}>
          <Plus size={16} /> Nuovo prezzo
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[["listini", "Listini e manodopera"], ["bolle", "Confronto bolle"], ["rating", "Rating fornitori"]].map(([key, label]) =>
          <button key={key} onClick={() => setSection(key)} className={`px-4 py-2 rounded text-sm font-semibold ${section === key ? "bg-slate-800 text-white" : "bg-white border border-slate-200"}`}>{label}</button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <select className={`${inputClass} max-w-xs`} value={supplier} onChange={e => setSupplier(e.target.value)}>
          <option value="">Tutti i fornitori</option>
          {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {section !== "rating" && (
          <div className="flex items-center gap-2 flex-1 min-w-48 border border-slate-300 rounded bg-white px-3">
            <Search size={16} className="text-slate-400" />
            <input className="w-full py-2 outline-none text-sm" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca materiale, servizio o fornitore" />
          </div>
        )}
      </div>

      {section === "listini" && (
        <div className="bg-white border border-slate-200 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs">
              <tr>{["Fornitore", "Tipo", "Prodotto / prestazione", "Unità", "Prezzo netto", "IVA", "Validità", "Azioni"].map(h => <th key={h} className="p-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {loadingPrices && <tr><td colSpan={8} className="p-6 text-center">Caricamento...</td></tr>}
              {!loadingPrices && filteredPrices.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500">Nessun prezzo registrato. Premi “Nuovo prezzo” per iniziare.</td></tr>}
              {filteredPrices.map(p => (
                <tr key={p.listino_id} className="border-t">
                  <td className="p-3 font-medium">{p.fornitore}</td>
                  <td className="p-3">{p.tipo === "manodopera" ? "Manodopera" : "Materiale"}</td>
                  <td className="p-3">{p.descrizione}{p.codice && <div className="text-xs text-slate-400">{p.codice}</div>}</td>
                  <td className="p-3">{p.unita_misura}</td>
                  <td className="p-3 font-semibold whitespace-nowrap">{euro(p.prezzo_unitario)}</td>
                  <td className="p-3">{p.aliquota_iva}%</td>
                  <td className="p-3">{p.data_validita || "—"}</td>
                  <td className="p-3 whitespace-nowrap">
                    <button title="Modifica" className="p-2" onClick={() => setEditing({ ...p })}><Pencil size={16} /></button>
                    <button title="Elimina" className="p-2 text-red-600" onClick={() => { if (window.confirm("Eliminare questo prezzo?")) deletePrice.mutate(p.listino_id); }}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {section === "bolle" && (
        <div className="bg-white border border-slate-200 rounded overflow-x-auto">
          <p className="p-3 text-xs text-slate-500">Il confronto automatico avviene solo quando fornitore, descrizione e unità di misura coincidono esattamente. I prodotti con nomi differenti non vengono abbinati automaticamente.</p>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs">
              <tr>{["Data", "Fornitore", "Materiale", "Quantità", "Prezzo bolla", "Listino", "Differenza"].map(h => <th key={h} className="p-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {loadingBolle && <tr><td colSpan={7} className="p-6 text-center">Caricamento bolle...</td></tr>}
              {!loadingBolle && filteredBolle.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Nessuna bolla trovata.</td></tr>}
              {filteredBolle.map((b, i) => {
                const p = matchingPrice(b);
                const validPrice = b.prezzo_unitario != null && Number(b.prezzo_unitario) > 0;
                const delta = p && validPrice ? Number(b.prezzo_unitario) - Number(p.prezzo_unitario) : null;
                return (
                  <tr key={b.bolla_id || i} className="border-t">
                    <td className="p-3">{b.data}</td>
                    <td className="p-3">{b.fornitore}</td>
                    <td className="p-3">{b.materiale}</td>
                    <td className="p-3">{b.quantita} {b.unita_misura}</td>
                    <td className="p-3 font-semibold">{validPrice ? euro(b.prezzo_unitario) : "Non indicato"}</td>
                    <td className="p-3">{p ? euro(p.prezzo_unitario) : "Non abbinato"}</td>
                    <td className={`p-3 font-semibold ${delta > 0 ? "text-red-600" : delta < 0 ? "text-emerald-700" : ""}`}>{delta === null ? "—" : `${delta > 0 ? "+" : ""}${euro(delta)}`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {section === "rating" && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {suppliers.filter(s => !supplier || norm(s) === norm(supplier)).map(s => {
            const r = ratings.find(v => norm(v.fornitore) === norm(s));
            return (
              <div key={s} className="bg-white border border-slate-200 rounded p-4 space-y-3">
                <div className="font-bold">{s}</div>
                <div className="flex items-center gap-2 text-amber-600"><Star size={18} fill="currentColor" /><span className="font-bold">{r ? `${Number(r.media).toFixed(2)} / 5` : "Non valutato"}</span></div>
                {r && <div className="text-xs text-slate-600">Qualità {r.qualita}/5 · Prezzo {r.prezzo}/5 · Puntualità {r.puntualita}/5 · Affidabilità {r.affidabilita}/5</div>}
                {r?.note && <p className="text-xs text-slate-500">{r.note}</p>}
                <button className={buttonClass} onClick={() => {
                  setRatingSupplier(s);
                  setRatingForm(r ? { qualita: r.qualita, prezzo: r.prezzo, puntualita: r.puntualita, affidabilita: r.affidabilita, note: r.note || "" } : { qualita: 3, prezzo: 3, puntualita: 3, affidabilita: 3, note: "" });
                }}>Valuta fornitore</button>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form className="bg-white rounded p-5 w-full max-w-xl max-h-[90vh] overflow-y-auto space-y-3" onSubmit={e => {
            e.preventDefault();
            const data = { ...editing, prezzo_unitario: Number(editing.prezzo_unitario), aliquota_iva: Number(editing.aliquota_iva) };
            savePrice.mutate({ id: editing.listino_id, data });
          }}>
            <div className="flex justify-between items-center"><h3 className="font-bold text-lg">{editing.listino_id ? "Modifica prezzo" : "Nuovo prezzo"}</h3><button type="button" onClick={() => setEditing(null)}><X /></button></div>
            <label className="block text-sm">Fornitore
              <input list="fornitori-lista" required className={inputClass} value={editing.fornitore} onChange={e => setEditing({ ...editing, fornitore: e.target.value })} />
              <datalist id="fornitori-lista">{suppliers.map(s => <option key={s} value={s} />)}</datalist>
            </label>
            <label className="block text-sm">Tipo
              <select className={inputClass} value={editing.tipo} onChange={e => setEditing({ ...editing, tipo: e.target.value, unita_misura: e.target.value === "manodopera" ? "ora" : "pz" })}>
                <option value="materiale">Materiale / prodotto</option><option value="manodopera">Manodopera / prestazione</option>
              </select>
            </label>
            <label className="block text-sm">Descrizione<input required className={inputClass} value={editing.descrizione} onChange={e => setEditing({ ...editing, descrizione: e.target.value })} /></label>
            <label className="block text-sm">Codice articolo<input className={inputClass} value={editing.codice} onChange={e => setEditing({ ...editing, codice: e.target.value })} /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">Unità di misura
                <select className={inputClass} value={editing.unita_misura} onChange={e => setEditing({ ...editing, unita_misura: e.target.value })}>
                  {["pz", "kg", "t", "m", "m²", "m³", "l", "sacco", "ora", "giorno", "corpo"].map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </label>
              <label className="block text-sm">Prezzo netto (€)<input required min="0" step="0.01" type="number" className={inputClass} value={editing.prezzo_unitario} onChange={e => setEditing({ ...editing, prezzo_unitario: e.target.value })} /></label>
              <label className="block text-sm">IVA (%)<input min="0" max="100" step="0.01" type="number" className={inputClass} value={editing.aliquota_iva} onChange={e => setEditing({ ...editing, aliquota_iva: e.target.value })} /></label>
              <label className="block text-sm">Data validità<input type="date" className={inputClass} value={editing.data_validita} onChange={e => setEditing({ ...editing, data_validita: e.target.value })} /></label>
            </div>
            <label className="block text-sm">Note<textarea className={inputClass} value={editing.note} onChange={e => setEditing({ ...editing, note: e.target.value })} /></label>
            <button disabled={savePrice.isPending} className="bg-slate-800 text-white px-4 py-2 rounded flex items-center gap-2"><Save size={16} /> Salva prezzo</button>
          </form>
        </div>
      )}

      {ratingSupplier && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form className="bg-white rounded p-5 w-full max-w-md space-y-4" onSubmit={e => {
            e.preventDefault();
            saveRating.mutate({ fornitore: ratingSupplier, ...ratingForm }, { onSuccess: () => setRatingSupplier("") });
          }}>
            <div className="flex justify-between items-center"><h3 className="font-bold">Valuta {ratingSupplier}</h3><button type="button" onClick={() => setRatingSupplier("")}><X /></button></div>
            {[["qualita", "Qualità"], ["prezzo", "Convenienza"], ["puntualita", "Puntualità"], ["affidabilita", "Affidabilità"]].map(([key, label]) => (
              <label key={key} className="block text-sm">{label}
                <select className={inputClass} value={ratingForm[key]} onChange={e => setRatingForm({ ...ratingForm, [key]: Number(e.target.value) })}>
                  {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} / 5</option>)}
                </select>
              </label>
            ))}
            <label className="block text-sm">Note<textarea className={inputClass} value={ratingForm.note} onChange={e => setRatingForm({ ...ratingForm, note: e.target.value })} /></label>
            <button disabled={saveRating.isPending} className="bg-slate-800 text-white px-4 py-2 rounded flex items-center gap-2"><Save size={16} /> Salva valutazione</button>
          </form>
        </div>
      )}
    </div>
  );
}
